import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

const SOLUTIONS = [
  { to: "/solutions/golf-course-lighting", label: "Golf Course Lighting" },
  { to: "/solutions/driving-range-lighting", label: "Driving Range Lighting" },
  { to: "/solutions/sports-lighting", label: "Sports Lighting" },
  { to: "/solutions/commercial-lighting", label: "Commercial Lighting" },
];

export const LogoMark = ({ lit = false }) => (
  <span className="relative inline-flex items-center justify-center w-9 h-9 bg-white rounded-sm overflow-hidden shrink-0">
    <img src="/assets/fairlume-logo.jpg" alt="Fairlume logo" className="w-7 h-7 object-cover" />
    <span
      className={`absolute inset-0 bg-gradient-to-r from-transparent via-[#FFC857]/60 to-transparent -translate-x-full ${
        lit ? "transition-transform duration-[1600ms] ease-out translate-x-full" : ""
      }`}
    />
  </span>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const [lit, setLit] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", onScroll, { passive: true });
    const onLit = () => {
      setLit(true);
      setTimeout(() => setLit(false), 2200);
    };
    window.addEventListener("fairlume:lit", onLit);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("fairlume:lit", onLit);
    };
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 ${
      isActive ? "text-white" : "text-white/60 hover:text-white"
    }`;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1C2220]/85 backdrop-blur-xl border-b hairline"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <LogoMark lit={lit} />
          <span className="font-display font-bold tracking-[0.18em] text-sm text-white">
            FAIRLUME
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <div
            className="relative"
            onMouseEnter={() => setSolOpen(true)}
            onMouseLeave={() => setSolOpen(false)}
          >
            <button
              data-testid="nav-solutions-dropdown"
              className="flex items-center gap-1 text-sm tracking-wide text-white/60 hover:text-white transition-colors duration-300"
              aria-expanded={solOpen}
            >
              Solutions <ChevronDown size={14} className={`transition-transform duration-300 ${solOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${
                solOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"
              }`}
            >
              <div className="bg-[#161B19]/95 backdrop-blur-xl border hairline rounded-md py-2 w-64 shadow-2xl">
                {SOLUTIONS.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`nav-solution-${s.to.split("/").pop()}`}
                    className="block px-5 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/solutions/golf-course-lighting" className={linkCls} data-testid="nav-golf-lighting">
            Golf Course Lighting
          </NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-get-quote-btn"
            onClick={() => navigate("/quote")}
            className="hidden sm:inline-flex items-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-white transition-colors duration-300"
          >
            Get a Quote <ArrowUpRight size={15} />
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#101214]/98 backdrop-blur-xl border-t hairline" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <Link to="/" onClick={() => setOpen(false)} className="text-white/80 text-base" data-testid="mobile-nav-home">Home</Link>
            {SOLUTIONS.map((s) => (
              <Link key={s.to} to={s.to} onClick={() => setOpen(false)} className="text-white/80 text-base" data-testid={`mobile-nav-${s.to.split("/").pop()}`}>
                {s.label}
              </Link>
            ))}
            <Link to="/about" onClick={() => setOpen(false)} className="text-white/80 text-base" data-testid="mobile-nav-about">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="text-white/80 text-base" data-testid="mobile-nav-contact">Contact</Link>
            <button
              data-testid="mobile-nav-quote-btn"
              onClick={() => { setOpen(false); navigate("/quote"); }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold px-5 py-3 rounded-sm"
            >
              Get a Quote <ArrowUpRight size={15} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
